import type { ReleaseManifestResponse } from "@nlkb/api-contract/releases";

export const installerTargets = [
  {
    label: "Mac · Apple silicon",
    operatingSystem: "macos",
    architecture: "aarch64",
  },
  {
    label: "Mac · Intel",
    operatingSystem: "macos",
    architecture: "x86_64",
  },
  {
    label: "Windows · x64",
    operatingSystem: "windows",
    architecture: "x86_64",
  },
] as const;

export type InstallerTarget = (typeof installerTargets)[number];

export function assertDesktopInstallerMatches(
  release: ReleaseManifestResponse,
  target: InstallerTarget,
): void {
  const artifact = release.manifest.artifact;
  if (
    artifact.kind !== "desktop-installer" ||
    artifact.operating_system !== target.operatingSystem ||
    artifact.architecture !== target.architecture ||
    artifact.signature !== null
  ) {
    throw new Error("installer_target_mismatch");
  }
}

export type InstallerLetterCopy = {
  readonly greeting: string;
  readonly body: string;
  readonly closing: string;
  readonly name: string;
  readonly title: string;
};

export function installerLetterCopy(
  operatingSystem: string,
): InstallerLetterCopy {
  const shared = {
    greeting: "Thanks for trying the desktop app this early.",
    closing: "Thank you,",
    name: "Logan",
    title: "Founder, Northern Logic",
  } as const;
  if (operatingSystem === "windows") {
    return {
      ...shared,
      body: "It isn’t Windows-signed yet, so SmartScreen may say Windows protected your PC the first time you run it. That’s expected.",
    };
  }
  return {
    ...shared,
    body: "It isn’t Apple-signed yet, so the first time you open it, macOS will warn that it can’t be verified. That’s expected.",
  };
}

export function installerApprovalSteps(
  operatingSystem: string,
): readonly string[] {
  if (operatingSystem === "windows") {
    return [
      "Download and run the installer.",
      "If Windows protected your PC appears, click More info.",
      "Click Run anyway.",
    ];
  }
  return [
    "Download the installer, open the disk image, and move the app to Applications.",
    "Open the app. When macOS says Apple could not verify it, click Done, not Move to Trash.",
    "Open System Settings → Privacy & Security, scroll to the bottom, and click Open Anyway.",
  ];
}

export function installerApprovalCopy(operatingSystem: string): string {
  if (operatingSystem === "windows") {
    return "Compare this SHA-256 in PowerShell with Get-FileHash -Algorithm SHA256. Do not approve a different file or checksum.";
  }
  return "Compare this SHA-256 with shasum -a 256. Do not approve a different file or checksum.";
}

export function startInstallerDownload(release: ReleaseManifestResponse): void {
  const link = document.createElement("a");
  link.href = release.download_url;
  link.download = release.manifest.artifact.filename;
  link.rel = "noopener";
  link.referrerPolicy = "no-referrer";
  document.body.append(link);
  link.click();
  link.remove();
}
